import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import { backendClient } from "@/lib/backendClient";
import { useSessionStore } from "@/store/session";
import { useSelectionStore } from "@/store/selection";
import { useOrderStore } from "@/store/order";
import { useProducts } from "@/models/product";
import type { ProductType } from "@/models/product";
import { parseSunProtectionOrderResponse } from "../mappers/order.parse";
import { hydrateSelectionFromOrder } from "../mappers/hydrate";
import GET_SUN_PROTECTION_ORDER from "../graphql/GetSunProtectionOrder.graphql";

type UseRestoreOrderResult = {
    loading: boolean;
    error: Error | null;
};

export function useRestoreOrder(): UseRestoreOrderResult {
    const backendDealId = useSessionStore((s) => s.backendDealId);
    const hasRestored = useRef(false);

    const {
        data: orderData,
        loading,
        error,
    } = useQuery<unknown>(GET_SUN_PROTECTION_ORDER, {
        client: backendClient,
        variables: { dealId: backendDealId },
        skip: !backendDealId,
    });

    const order = useMemo(
        () => parseSunProtectionOrderResponse(orderData),
        [orderData],
    );

    const activeCategory: ProductType | null =
        order?.products[0]?.productType ?? null;
    const { products } = useProducts(activeCategory);

    useEffect(() => {
        if (hasRestored.current) return;
        if (!order) return;
        const hydrated = hydrateSelectionFromOrder(order, products);
        if (!hydrated) return;

        const existing = useSelectionStore.getState();
        const sameActive =
            existing.selectedCategory === hydrated.selectedCategory &&
            existing.selectedTypeId === hydrated.selectedTypeId;
        const mergedActive = sameActive
            ? {
                  selectedProductIds: [
                      ...hydrated.activeSnapshot.selectedProductIds,
                      ...existing.selectedProductIds.filter(
                          (id) =>
                              !hydrated.activeSnapshot.selectedProductIds.includes(
                                  id,
                              ),
                      ),
                  ],
                  windowSelections: {
                      ...existing.windowSelections,
                      ...hydrated.activeSnapshot.windowSelections,
                  },
              }
            : hydrated.activeSnapshot;

        useSelectionStore.setState({
            selectedCategory: hydrated.selectedCategory,
            selectedTypeId: hydrated.selectedTypeId,
            selectedProductIds: mergedActive.selectedProductIds,
            windowSelections: mergedActive.windowSelections,
            cachedByCombo: {
                ...existing.cachedByCombo,
                ...hydrated.cachedByCombo,
            },
            lastTypeIdByCategory: {
                ...existing.lastTypeIdByCategory,
                ...hydrated.lastTypeIdByCategory,
            },
        });

        if (order.signature?.url) {
            useOrderStore.getState().setSignatureUrl(order.signature.url);
        }
        useOrderStore.getState().setOrderId(order.id);
        useOrderStore.getState().setHasPreviousOrder(true);
        useOrderStore
            .getState()
            .setIsMeasurementSigned(order.measurementFormSigned);

        hasRestored.current = true;
    }, [order, products]);

    return {
        loading,
        error: (error as Error | undefined) ?? null,
    };
}
